import {
  LayoutDashboard,
  Terminal,
  Box,
  Database,
  Monitor,
  Globe,
  Cloud,
} from 'lucide-react';
import type { TourDefinition } from './types';
import { BRAND } from '@/brand';

// Feature-wise tour catalogue. Each entry is a self-contained walkthrough the
// user can start, pause, resume, or restart. Every tour begins with a centered
// intro step and ends with a centered outro; the anchored steps in between target
// data-tour="..." attributes placed on each module's page.
export const TOURS: TourDefinition[] = [
  {
    id: 'dashboard',
    title: 'Dashboard overview',
    description: 'Learn what every card and chart on the dashboard tells you.',
    icon: LayoutDashboard,
    route: '/dashboard',
    steps: [
      {
        placement: 'center',
        title: `Welcome to ${BRAND.name}`,
        content:
          "This quick tour walks you through the dashboard. You can pause any time and pick up where you left off from the Getting Started page. Let's go!",
      },
      {
        target: '[data-tour="dashboard-stats"]',
        placement: 'bottom',
        title: 'Your environment at a glance',
        content:
          'These cards summarise your registered resources, currently active sessions, and access attempts over the last 24 hours — including how many were denied.',
      },
      {
        target: '[data-tour="dashboard-timeline"]',
        placement: 'bottom',
        title: 'Access attempts over time',
        content:
          'This timeline plots allowed vs. denied access attempts hour by hour, so you can spot spikes or unusual denial patterns quickly.',
      },
      {
        target: '[data-tour="dashboard-health"]',
        placement: 'left',
        title: 'Resource health',
        content:
          'A live breakdown of healthy, unhealthy, and unknown resources. Anything unhealthy usually means an agent lost its connection to the hub.',
      },
      {
        target: '[data-tour="dashboard-resources-by-type"]',
        placement: 'top',
        title: 'Resources by type',
        content:
          'See how your resources are distributed across databases, HTTP proxies, SSH hosts, and Kubernetes clusters.',
      },
      {
        target: '[data-tour="dashboard-recent-access"]',
        placement: 'top',
        title: 'Recent access attempts',
        content:
          'The latest connection attempts with who tried, what they did, and whether it was allowed or denied — a fast way to audit activity.',
      },
      {
        target: '[data-tour="dashboard-active-sessions"]',
        placement: 'top',
        title: 'Active sessions',
        content:
          'Sessions happening right now across SSH, kubectl, RDP, and databases. Every session is brokered and recorded by the gateway.',
      },
      // The shell (cascade plan 3.6): data-tour hooks ds-zta/AppHeader renders.
      {
        target: '[data-tour="theme"]',
        placement: 'bottom',
        title: 'Your account',
        content:
          'Your name and active role. The menu also switches between light and dark, or follows your system, and signs you out.',
      },
      {
        target: '[data-tour="help"]',
        placement: 'bottom',
        title: 'Replay any time',
        content:
          'Help starts the tour for the page you are on. On a page without one, it opens Getting Started with every tour.',
      },
      {
        placement: 'center',
        title: "You're all set",
        content:
          'That covers the dashboard. Head to Getting Started to install agents and register resources — and to take more feature tours.',
      },
    ],
  },
  {
    id: 'ssh',
    title: 'SSH resources',
    description: 'Enroll a Linux host with the SSH agent and connect through the gateway.',
    icon: Terminal,
    route: '/ssh-resources',
    steps: [
      {
        placement: 'center',
        title: 'Welcome — SSH Resources',
        content:
          `This page lets you enroll Linux hosts via the SSH agent and connect to them securely through ${BRAND.name}. Let's walk through the layout.`,
      },
      {
        target: '[data-tour="ssh-header"]',
        placement: 'bottom',
        title: 'Page header',
        content:
          'The header names the module and offers a refresh control to reload the SSH servers and agent status on demand.',
      },
      {
        target: '[data-tour="ssh-stats"]',
        placement: 'bottom',
        title: 'At-a-glance stats',
        content:
          'These cards summarize total SSH resources, online agents, active sessions, and the health of the Agent Hub.',
      },
      {
        target: '[data-tour="ssh-search"]',
        placement: 'bottom',
        title: 'Search and filter',
        content:
          'Search servers by name, host, or label, and optionally filter the list by one of your assigned roles.',
      },
      {
        target: '[data-tour="ssh-list"]',
        placement: 'top',
        title: 'SSH servers list',
        content:
          'Each enrolled server appears here with its endpoint, online status, sessions, and last-seen time, plus Terminal and CLI actions to connect through the gateway.',
      },
      {
        placement: 'center',
        title: "You're set",
        content:
          'You now know how to find, monitor, and connect to your SSH servers through the gateway.',
      },
    ],
  },
  {
    id: 'kubernetes',
    title: 'Kubernetes resources',
    description: 'Register a cluster via the in-cluster agent or a kubeconfig.',
    icon: Box,
    route: '/k8s-resources',
    steps: [
      {
        placement: 'center',
        title: 'Welcome — Kubernetes Resources',
        content:
          `This page lets you register Kubernetes clusters, monitor their status, and connect to them securely through ${BRAND.name}. This quick tour covers the main areas.`,
      },
      {
        target: '[data-tour="kubernetes-header"]',
        placement: 'bottom',
        title: 'Page header',
        content:
          'The header shows the module title and description. Manage your Kubernetes clusters and access them securely through the gateway from here.',
      },
      {
        target: '[data-tour="kubernetes-add"]',
        placement: 'bottom',
        title: 'Add a cluster',
        content:
          'Use Add Cluster to register a new Kubernetes cluster, either via the in-cluster agent or by uploading a kubeconfig. This button is shown to users with resource-management permissions.',
      },
      {
        target: '[data-tour="kubernetes-stats"]',
        placement: 'bottom',
        title: 'Cluster summary',
        content:
          'These cards summarize your clusters: the total registered, how many are currently online/healthy, and how many are unhealthy.',
      },
      {
        target: '[data-tour="kubernetes-search"]',
        placement: 'bottom',
        title: 'Search and filter',
        content:
          'Search clusters by name, API server, or labels. If you hold secondary roles, a role selector also appears here to filter by role.',
      },
      {
        target: '[data-tour="kubernetes-list"]',
        placement: 'top',
        title: 'Cluster list',
        content:
          'This card lists your clusters with Online/Agent status and last-seen time. Open a browser kubectl terminal for agent clusters, or view CLI connect commands for kubeconfig clusters.',
      },
      {
        placement: 'center',
        title: "You're set",
        content:
          'That’s the tour. Add a cluster, monitor its status, and connect via the web terminal or CLI. Relaunch this tour anytime from the button next to the page title.',
      },
    ],
  },
  {
    id: 'database',
    title: 'Databases',
    description: 'Add a database resource and connect with short-lived tunnels.',
    icon: Database,
    route: '/resources',
    steps: [
      {
        placement: 'center',
        title: 'Welcome — Databases',
        content:
          'This tour walks through registering a database resource on the Resources page and how users then connect to it over a short-lived tunnel using the iap CLI.',
      },
      {
        target: '[data-tour="database-header"]',
        placement: 'bottom',
        title: 'Resources overview',
        content:
          'The Resources page is where you register and manage all resources, including databases. Each registered resource can have its own access policies.',
      },
      {
        target: '[data-tour="database-add"]',
        placement: 'bottom',
        title: 'Add a database',
        content:
          'Click Add Resource to open the form. For a database you choose the engine, then enter the host, port, and credentials, and test the connection before saving.',
      },
      {
        target: '[data-tour="database-filter"]',
        placement: 'bottom',
        title: 'Search and filter',
        content:
          'Use the search box to find resources by name or type. The filters here narrow the list shown in the table below.',
      },
      {
        target: '[data-tour="database-type"]',
        placement: 'bottom',
        title: 'Filter by type',
        content: 'Select Database from the type dropdown to show only registered database resources.',
      },
      {
        target: '[data-tour="database-list"]',
        placement: 'top',
        title: 'Your resources',
        content:
          "Registered resources appear in this table. For a database, use the Connect action to get the CLI commands — users connect via a short-lived tunnel with 'iap db connect'.",
      },
      {
        placement: 'center',
        title: "You're set",
        content:
          'You now know how to register a database resource, filter the list, and how users connect to it through a short-lived tunnel. Launch this tour again anytime from the button beside the page title.',
      },
    ],
  },
  {
    id: 'desktop',
    title: 'Desktop (RDP) access',
    description: 'Publish a Windows/Linux desktop streamed to the browser.',
    icon: Monitor,
    route: '/desktop-resources',
    steps: [
      {
        placement: 'center',
        title: 'Welcome — Desktop Access',
        content:
          `This page lets you publish and connect to Windows and Linux desktops over RDP, streamed directly to your browser through ${BRAND.name}. This short tour highlights the key controls.`,
      },
      {
        target: '[data-tour="desktop-header"]',
        placement: 'bottom',
        title: 'Desktop Access overview',
        content:
          `This is the Desktop Access module, where published desktops are connected to via RDP through ${BRAND.name}. Use the refresh control here to reload the latest desktop list and health status.`,
      },
      {
        target: '[data-tour="desktop-add"]',
        placement: 'bottom',
        title: 'Add a desktop',
        content:
          'Click Add Desktop to register a new RDP resource with its host, port, and credentials. This button is available to users with resource-management permissions.',
      },
      {
        target: '[data-tour="desktop-search"]',
        placement: 'bottom',
        title: 'Search desktops',
        content: 'Filter the desktop list by name or IP address. Results update as you type.',
      },
      {
        target: '[data-tour="desktop-list"]',
        placement: 'top',
        title: 'Your desktops',
        content:
          'Published desktops appear here as cards showing health status, connection details, and access state. Approved desktops offer a Connect via RDP button that opens an in-browser session; sessions are recorded.',
      },
      {
        placement: 'center',
        title: "You're set",
        content:
          'You now know how to add, find, and connect to desktops over RDP. Relaunch this tour any time from the button beside the page title.',
      },
    ],
  },
  {
    id: 'proxy',
    title: 'Proxy (HTTP) resources',
    description: 'Expose an internal web app behind SSO at a gateway URL.',
    icon: Globe,
    route: '/proxy-resources',
    steps: [
      {
        placement: 'center',
        title: 'Welcome — Proxy Resources',
        content:
          'This page lets you publish internal HTTP(S) applications at a gateway URL with single sign-on enforced in front of them. Each proxy resource maps an upstream service to a public subdomain and path, secured by bound roles. This quick tour walks through the main controls.',
      },
      {
        target: '[data-tour="proxy-header"]',
        placement: 'bottom',
        title: 'Page header',
        content:
          `Manage HTTP proxy resources and routing configuration for ${BRAND.name}. The header also holds Import, Export, and Refresh actions for the routing config.`,
      },
      {
        target: '[data-tour="proxy-add"]',
        placement: 'left',
        title: 'Add a proxy resource',
        content:
          'Click here to open the create dialog. You define an upstream URL, an optional subdomain and domain, a port, comma-separated path prefixes, and a routing priority.',
      },
      {
        target: '[data-tour="proxy-search"]',
        placement: 'bottom',
        title: 'Search and filter',
        content:
          'Filter resources by name or type as you type. When you have secondary roles, a role selector appears here to scope the list to a specific role.',
      },
      {
        target: '[data-tour="proxy-list"]',
        placement: 'top',
        title: 'Resource list',
        content:
          'Each proxy resource is listed with its name, type, access URL, priority, and health status. Use the row actions to edit or delete a resource; an empty state appears here when none exist.',
      },
      {
        placement: 'center',
        title: "You're set",
        content:
          'You now know how to publish and manage proxy resources. Add a resource to expose an internal app at a gateway URL with SSO enforced. You can relaunch this tour anytime from the header.',
      },
    ],
  },
  {
    id: 'cloud',
    title: 'Cloud providers',
    description: 'Connect an AWS, Azure, GCP, or Hetzner account.',
    icon: Cloud,
    route: '/cloud-providers',
    steps: [
      {
        placement: 'center',
        title: 'Welcome — Cloud Providers',
        content:
          "This page lets you connect cloud accounts so the gateway can broker identity-based access to their consoles and resources. Let's take a quick tour.",
      },
      {
        target: '[data-tour="cloud-header"]',
        placement: 'bottom',
        title: 'Cloud Providers',
        content:
          'The header identifies this module, where you manage cloud accounts and discover resources across providers. Replay this tour anytime from the launcher here.',
      },
      {
        target: '[data-tour="cloud-providers"]',
        placement: 'bottom',
        title: 'Provider grid',
        content:
          'Each supported provider is shown as a card. Active providers (AWS, Azure, Hetzner, GCP) are clickable; others are marked Coming Soon.',
      },
      {
        target: '[data-tour="cloud-aws"]',
        placement: 'right',
        title: 'A provider card',
        content:
          'This Amazon Web Services card shows the connected account count and status. Click it to Get Started when unconfigured, or Manage once accounts are connected.',
      },
      {
        target: '[data-tour="cloud-info"]',
        placement: 'top',
        title: 'Multi-cloud zero trust',
        content:
          `${BRAND.name} provides unified identity-based access control across all your cloud providers. Add an account, enable services, and discover resources, then control access through roles and access requests.`,
      },
      {
        placement: 'center',
        title: "You're set",
        content:
          'That’s the Cloud Providers page. Pick a provider card to connect an account and start brokering access.',
      },
    ],
  },
];

export function getTour(id: string): TourDefinition | undefined {
  return TOURS.find((t) => t.id === id);
}
